<template>
  <div
    :class="{
      'carousel-fi': true,
      'carousel-fi--no-pagination': !carouselSettings.pagination,
      'carousel-fi--destroyed': isDestroyed,
      'carousel-fi--clipped-right': clippedRight,
      'carousel-fi--ssr': !isMounted,
      [`carousel-fi--theme-${theme}`]: theme,
    }"
  >
    <splide
      ref="carousel"
      :options="carouselSettings"
      :has-track="false"
      @splide:mounted="onMounted"
      @splide:move="beforeChange"
      @splide:moved="afterChange"
      @splide:click="click"
      @splide:active="active"
      @splide:destroy="isDestroyed = true"
      @splide:drag="onDrag"
      @splide:dragged="onDragged"
    >
      <splide-track class="carousel-fi__track">
        <slot />
      </splide-track>

      <div
        v-if="hasArrows && isActive"
        class="carousel-fi__arrows"
      >
        <div
          :class="{
            'carousel-fi__arrow-wrapper': true,
            'carousel-fi__arrow-wrapper--prev': true,
            'carousel-fi__arrow-wrapper--disabled': isPrevDisabled,
            'carousel-fi__arrow-wrapper--animating': isAnimating,
          }"
          @click="prev"
        >
          <slot name="prevArrow">
            <div class="carousel-fi__arrow">
              <arrow-icon />
            </div>
          </slot>
        </div>

        <div
          :class="{
            'carousel-fi__arrow-wrapper': true,
            'carousel-fi__arrow-wrapper--next': true,
            'carousel-fi__arrow-wrapper--disabled': isNextDisabled,
            'carousel-fi__arrow-wrapper--animating': isAnimating,
          }"
          @click="next"
        >
          <slot name="nextArrow">
            <div class="carousel-fi__arrow">
              <arrow-icon />
            </div>
          </slot>
        </div>
      </div>

      <slot name="footer" />

      <ul
        v-if="carouselSettings.pagination"
        class="carousel-fi__pagination splide__pagination"
      ></ul>
    </splide>
  </div>
</template>

<script setup lang="ts">
import '@splidejs/vue-splide/css/core';

import { Splide, SplideTrack } from '@splidejs/vue-splide';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import ArrowIcon from '@/components/atoms/arrow-icon/ArrowIcon.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import screenWidths from '@/tailwind/screenWidths';

// The underlying Splide core instance, derived from the vue-splide wrapper.
type SplideInstance = NonNullable<InstanceType<typeof Splide>['splide']>;

interface Props {
  settings?: {
    type?: 'fade' | 'loop' | string;
    [key: string]: any;
  };
  clippedRight?: boolean;
  theme?: string;
  noArrows?: boolean;
  noPagination?: boolean;
  items?: any[];
  thumbnailCarouselRef?: any;
}

interface CarouselEmits {
  'after-change': [payload: { oldSlideIndex: number; newSlideIndex: number }];
  'before-change': [payload: { oldSlideIndex: number; newSlideIndex: number }];
  destroyed: [];
  edge: [payload: { left: boolean; right: boolean }];
  remounted: [];
  'slide-click': [payload: { index: number; element: HTMLElement }];
  swipe: [index: number];
  'splide-drag': [];
  'splide-dragged': [];
}

const props = withDefaults(defineProps<Props>(), {
  settings: () => ({}),
  clippedRight: false,
  theme: '',
  noArrows: false,
  noPagination: false,
  items: () => [],
  thumbnailCarouselRef: null,
});

const emit = defineEmits<CarouselEmits>();

const { isMD, isSmDialog, windowWidth } = useBreakpoints();

const defaultSettings = {
  rewind: true,
  rewindSpeed: 1000,
  speed: 1000,
  perPage: 1,
  perMove: 1,
  arrows: false,
  pagination: true,
  mediaQuery: 'min',
  lazyLoad: 'nearby',
};

const isPrevDisabled = ref(false);
const isNextDisabled = ref(false);
const isAnimating = ref(false);
const destroyed = ref(false);
const isMounted = ref(false);

const carousel = useTemplateRef<InstanceType<typeof Splide>>('carousel');
const isDestroyed = computed({
  get() {
    let state = destroyed.value;
    if (props.clippedRight) {
      state = isMD.value;
    }

    if (props.theme === 'retailer-slider') {
      state = !isSmDialog.value;
    }

    return state;
  },
  set(state: boolean) {
    destroyed.value = state;
  },
});

const clippedRightSettings = {
  rewind: false,
  pagination: false,
  breakpoints: {
    [screenWidths.sm]: {
      perPage: 2,
    },
    [screenWidths.md]: {
      destroy: true,
    },
  },
};

const carouselSettings = computed(() => {
  const settings = {
    ...defaultSettings,
    ...(props.clippedRight ? clippedRightSettings : {}),
    ...props.settings,
  };
  if (settings.rewind && settings.type !== 'fade') {
    settings.type = 'loop';
  }

  if (props.noPagination) {
    settings.pagination = false;
  }

  return settings;
});

const itemsLength = computed(() => props.items?.length);

const isActive = computed(() => {
  if (!carousel.value) {
    return false;
  }

  // For autoWidth mode the perPage concept is meaningless — slides have CSS-driven widths.
  // Measure actual DOM overflow instead.
  if (getActiveOption('autoWidth', carouselSettings.value)) {
    // reactive dependency so this recomputes on every resize
    void windowWidth.value; // NOSONAR

    if (!(carousel.value.length || itemsLength.value)) {
      return false;
    }

    const splideInstance = (carousel.value as any).splide as any;
    if (!splideInstance?.Components?.Elements) {
      return false;
    }
    const list = splideInstance.Components.Elements.list as HTMLElement | undefined;
    const track = splideInstance.Components.Elements.track as HTMLElement | undefined;
    if (!list || !track) {
      return false;
    }

    return list.scrollWidth > track.clientWidth;
  }

  return (
    (carousel.value.length || itemsLength.value) >
    getActiveOption('perPage', carouselSettings.value)
  );
});

const hasArrows = computed(() => {
  return !(
    props.noArrows ||
    (props.settings && getActiveOption('arrows', props.settings) === false)
  );
});

watch(
  isDestroyed,
  (state) => {
    if (state) {
      emit('destroyed');
    } else {
      emit('remounted');
    }
  },
  { immediate: true },
);

onMounted(() => {
  isMounted.value = true;
});

const prev = () => {
  if (isAnimating.value) {
    return;
  }

  if (props.thumbnailCarouselRef) {
    props.thumbnailCarouselRef.prev();
    return;
  }

  isAnimating.value = true;
  carousel.value?.go('<');
};

const next = () => {
  if (isAnimating.value) {
    return;
  }

  if (props.thumbnailCarouselRef) {
    props.thumbnailCarouselRef.next();
    return;
  }

  isAnimating.value = true;
  carousel.value?.go('>');
};

const go = (index: number) => {
  carousel.value?.go(index);
};

const getIndex = () => {
  if (IS_SSR) {
    return undefined;
  }
  return carousel.value?.splide?.index;
};

const refresh = () => {
  carousel.value?.splide.refresh();
};

const getSplide = () => carousel.value?.splide;

const sync = (target: SplideInstance) => {
  carousel.value?.sync(target);
};

const beforeChange = (_context: any, newIndex: number, prevIndex: number) => {
  isAnimating.value = true;
  emit('before-change', { oldSlideIndex: prevIndex, newSlideIndex: newIndex });
};

const afterChange = (_context: any, newIndex: number, prevIndex: number) => {
  isAnimating.value = false;
  emit('after-change', { oldSlideIndex: prevIndex, newSlideIndex: newIndex });
};

const onDrag = () => {
  emit('splide-drag');
};

const onDragged = () => {
  emit('splide-dragged');
};

const click = (_context: any, slideContext: { index: number; slide: HTMLElement }) => {
  emit('slide-click', { index: slideContext.index, element: slideContext.slide });
};

const active = (_context: any, { index }: { index: number }) => {
  emit('swipe', index);

  if (getActiveOption('rewind') || !carousel.value) {
    return;
  }

  isNextDisabled.value = false;
  isPrevDisabled.value = false;

  if (index + getActiveOption('perPage') === (carousel.value.length || itemsLength.value)) {
    isNextDisabled.value = true;
  } else if (index === 0) {
    isPrevDisabled.value = true;
  }

  if (isPrevDisabled.value || isNextDisabled.value) {
    emit('edge', { left: isPrevDisabled.value, right: isNextDisabled.value });
  }
};

const pause = () => {
  if (!carousel.value) {
    return;
  }

  const { Autoplay } = carousel.value.splide.Components;

  if (!Autoplay) {
    return;
  }

  Autoplay.pause();
};

const play = () => {
  if (!carousel.value) {
    return;
  }

  const { Autoplay } = carousel.value.splide.Components;

  if (!Autoplay) {
    return;
  }

  Autoplay.play();
};

const getActiveOption = (name: string, settings?: Record<string, any>) => {
  if (!carousel.value) {
    return null;
  }

  let setting = null;
  const options = settings ?? carousel.value.options;
  if (!options) {
    return null;
  }

  setting = options[name];
  if (options.breakpoints) {
    Object.entries(options.breakpoints).forEach(([key, value]) => {
      if (windowWidth.value >= Number.parseInt(key)) {
        if (typeof (value as any)[name] === 'undefined') {
          return;
        }

        setting = (value as any)[name];
      }
    });
  }

  return setting;
};

defineExpose({
  go,
  getIndex,
  getSplide,
  sync,
  refresh,
  pause,
  play,
  prev,
  next,
  isPrevDisabled,
  isNextDisabled,
  isActive,
  isAnimating,
});
</script>

<style scoped src="./carousel-fi" />

<style src="./carousel-fi-unscoped" />
